/**
 * Owner: Masa Bayu
 * File: bka-feature-flag-enablement.ts
 * Date:Time: 2026-09-22 18:35
 * Description: Slice F/G BKA flag enablement · kill switch · controlled then GA.
 */
import { assertNoForbiddenFoundationKey } from './authority';
import { BKA_ADAM_FEATURE_FLAG } from './bka-adam-types';
import { BKA_MANUSCRIPT_FEATURE_FLAG, BKA_PDF_FEATURE_FLAG } from './bka-manuscript-types';
import { BKA_POST_HANDOFF_FEATURE_FLAG } from './bka-post-handoff-types';
import { BKA_WAVE_A_FEATURE_FLAG } from './bka-wave-a-types';
import { assertTimestamp } from './guards';
export const BKA_SLICE_F_CONTROLLED_COHORT_ID = 'bka.production.controlled';
export const BKA_SLICE_G_GA_COHORT_ID = 'bka.production.ga';
export const BKA_FEATURE_FLAG_IDS = Object.freeze([
    BKA_WAVE_A_FEATURE_FLAG.id,
    BKA_MANUSCRIPT_FEATURE_FLAG.id,
    BKA_PDF_FEATURE_FLAG.id,
    BKA_ADAM_FEATURE_FLAG.id,
    BKA_POST_HANDOFF_FEATURE_FLAG.id,
]);
function assertFlagIds(ids) {
    const allowed = new Set(BKA_FEATURE_FLAG_IDS);
    for (const id of ids) {
        if (!allowed.has(id)) {
            throw new Error(`Unknown BKA feature flag id: ${id}`);
        }
    }
    return Object.freeze([...ids]);
}
/** Fail-closed policy — all flags off · Slice F/G incomplete. */
export function createBkaFeatureFlagsDisabledPolicy(input) {
    assertNoForbiddenFoundationKey(input ?? {}, 'BKA flags disabled policy');
    return Object.freeze({
        environment: input?.environment ?? 'production',
        cohort: 'none',
        cohortId: null,
        killSwitchActive: false,
        enabledFlagIds: Object.freeze([]),
        sliceFCompleted: false,
        sliceGCompleted: false,
        activatedAt: null,
        compileDefaultsRemainOff: true,
        failClosedDefault: true,
        mutatesQxw: false,
        mutatesQxp: false,
        inventsAuthority: false,
        doc03Undisturbed: true,
    });
}
/**
 * Slice F activation — controlled production cohort only.
 * Does not complete Slice G (Production GA). Compile-time defaults stay off.
 */
export function activateBkaSliceFControlledEnablement(input) {
    assertNoForbiddenFoundationKey(input, 'BKA Slice F controlled enablement');
    const environment = input.environment ?? 'production';
    if (environment !== 'production' && environment !== 'staging') {
        throw new Error('BKA Slice F requires staging|production environment');
    }
    const activatedAt = input.activatedAt ?? new Date().toISOString();
    assertTimestamp(activatedAt, 'BKA Slice F activatedAt');
    const killSwitchActive = input.killSwitchActive === true;
    const requested = assertFlagIds(input.flagIds ?? BKA_FEATURE_FLAG_IDS);
    const enabledFlagIds = killSwitchActive
        ? Object.freeze([])
        : requested;
    const policy = Object.freeze({
        environment,
        cohort: 'controlled',
        cohortId: BKA_SLICE_F_CONTROLLED_COHORT_ID,
        killSwitchActive,
        enabledFlagIds,
        sliceFCompleted: true,
        sliceGCompleted: false,
        activatedAt,
        compileDefaultsRemainOff: true,
        failClosedDefault: true,
        mutatesQxw: false,
        mutatesQxp: false,
        inventsAuthority: false,
        doc03Undisturbed: true,
    });
    return Object.freeze({
        policy,
        releaseId: input.releaseId ?? 'qx-bka-add-book@1.0.0',
        killSwitchPath: 'applyBkaFeatureFlagKillSwitch',
        authorityChainIntact: true,
        mutatesQxw: false,
        mutatesQxp: false,
    });
}
/** Immediate disable — kill switch (flags off · cohort / slice completion retained for audit). */
export function applyBkaFeatureFlagKillSwitch(policy) {
    assertNoForbiddenFoundationKey(policy, 'BKA flag kill switch');
    return Object.freeze({
        ...policy,
        killSwitchActive: true,
        enabledFlagIds: Object.freeze([]),
        mutatesQxw: false,
        mutatesQxp: false,
        inventsAuthority: false,
        doc03Undisturbed: true,
    });
}
/** Resolve whether a BKA flag is enabled under the active policy. */
export function isBkaFeatureFlagEnabled(policy, flagId) {
    assertNoForbiddenFoundationKey({ policy, flagId }, 'BKA flag resolve');
    if (policy.killSwitchActive)
        return false;
    if (policy.cohort === 'none')
        return false;
    if (!policy.enabledFlagIds.includes(flagId))
        return false;
    if (policy.cohort === 'ga')
        return policy.sliceGCompleted === true;
    // controlled
    return policy.sliceFCompleted === true;
}
/** Slice F verification gate — controlled enablement + kill switch · GA incomplete. */
export function runBkaSliceFEnablementGate(input) {
    assertNoForbiddenFoundationKey(input ?? {}, 'BKA Slice F enablement gate');
    const compileDefaultsRemainOff = BKA_WAVE_A_FEATURE_FLAG.defaultEnabledInProduction === false &&
        BKA_MANUSCRIPT_FEATURE_FLAG.defaultEnabledInProduction === false &&
        BKA_PDF_FEATURE_FLAG.defaultEnabledInProduction === false &&
        BKA_ADAM_FEATURE_FLAG.defaultEnabledInProduction === false &&
        BKA_POST_HANDOFF_FEATURE_FLAG.defaultEnabledInProduction === false;
    const record = activateBkaSliceFControlledEnablement({
        ...(input?.activatedAt !== undefined ? { activatedAt: input.activatedAt } : {}),
        ...(input?.releaseId !== undefined ? { releaseId: input.releaseId } : {}),
        ...(input?.environment !== undefined ? { environment: input.environment } : {}),
        killSwitchActive: false,
    });
    const policy = record.policy;
    const allListedFlagsEnabled = BKA_FEATURE_FLAG_IDS.every((id) => isBkaFeatureFlagEnabled(policy, id));
    const afterKillSwitch = applyBkaFeatureFlagKillSwitch(policy);
    const killSwitchClearsFlags = afterKillSwitch.killSwitchActive === true &&
        BKA_FEATURE_FLAG_IDS.every((id) => !isBkaFeatureFlagEnabled(afterKillSwitch, id));
    const ok = compileDefaultsRemainOff &&
        policy.sliceFCompleted === true &&
        policy.sliceGCompleted === false &&
        policy.cohort === 'controlled' &&
        policy.cohortId === BKA_SLICE_F_CONTROLLED_COHORT_ID &&
        allListedFlagsEnabled &&
        killSwitchClearsFlags &&
        record.authorityChainIntact === true &&
        record.mutatesQxw === false &&
        record.mutatesQxp === false &&
        policy.doc03Undisturbed === true;
    return Object.freeze({
        ok,
        sliceFCompleted: true,
        sliceGCompleted: false,
        cohort: 'controlled',
        cohortId: BKA_SLICE_F_CONTROLLED_COHORT_ID,
        flagsEnabledCount: policy.enabledFlagIds.length,
        allListedFlagsEnabled,
        killSwitchOperable: true,
        killSwitchClearsFlags,
        compileDefaultsRemainOff: true,
        authorityChainIntact: true,
        noProductionGa: true,
        doc03Undisturbed: true,
        mutatesQxw: false,
        mutatesQxp: false,
        record,
        afterKillSwitch,
    });
}
//# sourceMappingURL=bka-feature-flag-enablement.js.map